import { Mention, MentionBlot } from "quill-mention";
import "quill-mention/dist/quill.mention.css";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom/client";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchUsersDropdown } from "../store/Slices/dropdownSlice";
import { commentOnTrip, getTripHistory } from "../store/Slices/TripSlice";
import { formatDate, formatLogAfter, getCurrentUser } from "../utils/helper";
import {
    activeActivityIcon,
    activeAllIcon,
    activeCommentsIcon,
    activityIcon,
    allIcon,
    backButtonIcon,
    bulletPoint,
    commentsIcon,
    sendMsgIcon,
} from "../utils/svgs";
import { errorNotify } from "../utils/toast";
import UserAvatar from "./UserAvatar";

Quill.register({ "blots/mention": MentionBlot, "modules/mention": Mention });

const DEFAULT_EMPTY_MESSAGES = {
    all: "No history found",
    comment: "No comments found",
    logs: "No logs found",
};

const isObjectLike = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

const pickFirstText = (...values) => {
    for (const value of values) {
        const normalizedValue = typeof value === "string" ? value.trim() : "";
        if (normalizedValue) return normalizedValue;
    }

    return "";
};

const pickFirstArray = (...values) => {
    for (const value of values) {
        if (Array.isArray(value)) return value;
    }

    return [];
};

const getPrimaryCommentEntry = (entry = {}) => {
    if (Array.isArray(entry?.comments)) {
        const firstComment = entry.comments.find((comment) => isObjectLike(comment));
        if (firstComment) return firstComment;

        const firstStringComment = entry.comments.find((comment) => typeof comment === "string");
        if (typeof firstStringComment === "string") {
            return { comment: firstStringComment };
        }
    }

    if (isObjectLike(entry?.comments)) {
        return entry.comments;
    }

    return entry;
};

const isCommentItem = (item) => {
    const sourceType = String(item?.source_type || item?.type || "").trim().toLowerCase();
    if (sourceType === "comment") return true;
    if (sourceType === "log" || sourceType === "activity") return false;

    return (
        isObjectLike(item?.comments) ||
        Array.isArray(item?.comments) ||
        typeof item?.comments === "string" ||
        typeof item?.comment !== "undefined" ||
        typeof item?.message !== "undefined" ||
        Array.isArray(item?.parent) ||
        Array.isArray(item?.replies) ||
        Array.isArray(item?.children) ||
        typeof item?.parent_id !== "undefined"
    );
};

const isLogItem = (item) =>
    !isCommentItem(item) &&
    (item?.source_type === "log" || item?.type === "activity" || item?.type === "log");

const getCommentAuthor = (entry = {}) => {
    const commentSource = getPrimaryCommentEntry(entry);
    const author =
        commentSource?.user ||
        commentSource?.created_by_user ||
        commentSource?.createdByUser ||
        commentSource?.commented_by_user ||
        commentSource?.comment_by_user ||
        commentSource?.addresser ||
        commentSource?.author ||
        entry?.user ||
        entry?.created_by_user ||
        entry?.createdByUser ||
        entry?.commented_by_user ||
        entry?.comment_by_user ||
        {};

    return {
        id:
            author?.id ??
            commentSource?.user_id ??
            commentSource?.addresser_id ??
            entry?.user_id ??
            "",
        username:
            author?.username ||
            author?.name ||
            author?.full_name ||
            commentSource?.username ||
            commentSource?.name ||
            "-",
        image:
            author?.image ||
            author?.profile_image ||
            author?.avatar ||
            commentSource?.image ||
            "",
    };
};

const getCommentBody = (entry = {}) => {
    const commentSource = getPrimaryCommentEntry(entry);

    return (
        pickFirstText(
            commentSource?.comment,
            entry?.comment,
            typeof entry?.comments === "string" ? entry.comments : "",
            commentSource?.text,
            entry?.text,
            commentSource?.message,
            entry?.message,
            commentSource?.description,
            entry?.description,
            commentSource?.body,
            entry?.body,
            commentSource?.content,
            entry?.content
        ) || "-"
    );
};

const getCommentReplies = (entry = {}) => {
    const commentSource = getPrimaryCommentEntry(entry);
    return pickFirstArray(
        commentSource?.parent,
        commentSource?.replies,
        entry?.parent,
        entry?.replies,
        entry?.children
    );
};

const getCommentThreadId = (entry = {}) => {
    const commentSource = getPrimaryCommentEntry(entry);
    return (
        commentSource?.id ??
        entry?.id ??
        entry?.comment_id ??
        commentSource?.comment_id ??
        null
    );
};

const getCommentParentId = (entry = {}) => {
    const commentSource = getPrimaryCommentEntry(entry);
    return (
        commentSource?.parent_id ??
        entry?.parent_id ??
        commentSource?.reply_to_id ??
        entry?.reply_to_id ??
        0
    );
};

const getCommentCreatedAt = (entry = {}) => {
    const commentSource = getPrimaryCommentEntry(entry);
    return (
        commentSource?.created_at ||
        entry?.created_at ||
        commentSource?.updated_at ||
        entry?.updated_at ||
        ""
    );
};

const findCommentInThread = (entry = {}, targetId) => {
    const normalizedTargetId = String(targetId ?? "").trim();
    if (!normalizedTargetId) return null;

    const currentId = String(getCommentThreadId(entry) ?? "").trim();
    if (currentId && currentId === normalizedTargetId) {
        return entry;
    }

    const nestedReplies = getCommentReplies(entry);
    for (const reply of nestedReplies) {
        const matchedReply = findCommentInThread(reply, normalizedTargetId);
        if (matchedReply) return matchedReply;
    }

    return null;
};

const getReplyTargetLabel = (reply = {}, rootComment = {}) => {
    const parentId = String(getCommentParentId(reply) ?? "").trim();
    if (!parentId || parentId === "0") return "";

    const matchedParent = findCommentInThread(rootComment, parentId);
    if (!matchedParent) return "";

    const parentAuthor = getCommentAuthor(matchedParent);
    return parentAuthor?.username || "";
};

const LogsComments = ({
    staticHistoryItems = null,
    historyItems = null,
    commentValue: controlledCommentValue,
    onCommentChange,
    onSendComment,
    onFetchHistory,
    onSubmitComment,
    editorModules,
    editorFormats,
    emptyMessages = DEFAULT_EMPTY_MESSAGES,
    loading = false,
    hideComposer = false,
}) => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const currentUser = getCurrentUser();
    const isStaticMode = Array.isArray(staticHistoryItems);
    const isExternalHistoryMode =
        !isStaticMode &&
        (Array.isArray(historyItems) || typeof onFetchHistory === "function" || typeof onSubmitComment === "function");
    const isControlledCommentMode = typeof onCommentChange === "function";

    const { tripHistoryData } = useSelector((state) => state.trips) || {};
    const { usersDropdownData: userDropdownData } = useSelector((state) => state.dropdowns);

    useEffect(() => {
        dispatch(fetchUsersDropdown());
    }, [dispatch]);

    const mentionUsers = (userDropdownData || []).map((user) => ({
        id: user.value,
        value: user.label,
        image: user.image,
        denotationChar: "@",
    }));

    const quillRef = useRef(null);

    const mentionModules = useMemo(
        () => ({
            toolbar: [["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["link"]],
            mention: {
                mentionDenotationChars: ["@"],
                source: (searchTerm, renderList) => {
                    const term = (searchTerm || "").toLowerCase();
                    const matches = mentionUsers.filter((user) =>
                        user.value.toLowerCase().includes(term)
                    );
                    renderList(matches, searchTerm);
                },
                onSelect(item, insertItem) {
                    insertItem(item);

                    const quill = quillRef.current?.getEditor();
                    if (quill) {
                        const range = quill.getSelection(true);
                        quill.insertText(range.index, " ");
                        quill.setSelection(range.index + 1, 0);
                    }
                },
                renderItem: (item) => {
                    const div = document.createElement("div");
                    div.style.display = "flex";
                    div.style.alignItems = "center";
                    div.style.gap = "8px";
                    div.style.padding = "4px 8px";

                    const avatarContainer = document.createElement("div");
                    ReactDOM.createRoot(avatarContainer).render(
                        <UserAvatar username={item.value} image={item.image} size={24} />
                    );

                    const span = document.createElement("span");
                    span.textContent = item.value;
                    div.appendChild(avatarContainer);
                    div.appendChild(span);
                    return div;
                },
            },
        }),
        [mentionUsers]
    );

    const mentionFormats = ["bold", "italic", "underline", "list", "bullet", "link", "mention"];

    const resolvedModules = editorModules || mentionModules;
    const resolvedFormats = editorFormats || mentionFormats;

    const [tab, setTab] = useState("all");
    const [commentValue, setCommentValue] = useState("");
    const [parentId, setParentId] = useState(null);
    const [sending, setSending] = useState(false);
    const [threadComment, setThreadComment] = useState(null);
    const [threadReplies, setThreadReplies] = useState([]);

    const resolvedCommentValue = isControlledCommentMode ? controlledCommentValue || "" : commentValue;
    const handleCommentChange = isControlledCommentMode
        ? (value) => onCommentChange?.(value)
        : setCommentValue;

    const resolvedHistoryItems = isStaticMode
        ? staticHistoryItems
        : isExternalHistoryMode
            ? historyItems
            : tripHistoryData;

    const filteredItems = useMemo(() => {
        const source = Array.isArray(resolvedHistoryItems) ? resolvedHistoryItems : [];

        return source.filter((item) => {
            if (tab === "all") return true;
            if (tab === "comment") return isCommentItem(item);
            return isLogItem(item);
        });
    }, [resolvedHistoryItems, tab]);

    const extractMentionUserIds = (delta) => {
        const ids = [];
        if (!delta || !delta.ops) return ids;

        delta.ops.forEach((op) => {
            if (op.insert && op.insert.mention && op.insert.mention.id) {
                ids.push(op.insert.mention.id);
            }
        });

        return Array.from(new Set(ids));
    };

    const handleSend = async () => {
        if (!resolvedCommentValue.trim() || resolvedCommentValue === "<p><br></p>") return;

        if (isStaticMode) {
            onSendComment?.();
            return;
        }

        setSending(true);

        try {
            const quill = quillRef.current?.getEditor();
            const delta = quill ? quill.getContents() : null;
            const mention_user_ids = extractMentionUserIds(delta);

            if (isExternalHistoryMode) {
                await onSubmitComment?.({
                    comment: resolvedCommentValue,
                    parent_id: parentId ? parseInt(parentId, 10) : 0,
                    mention_user_ids,
                    history_type: tab,
                });
            } else {
                const payload = {
                    comment: resolvedCommentValue,
                    trip_id: parseInt(id, 10),
                    parent_id: parentId ? parseInt(parentId, 10) : 0,
                    mention_user_ids,
                };

                await dispatch(commentOnTrip(payload)).unwrap();
            }

            if (isControlledCommentMode) {
                onCommentChange?.("");
            } else {
                setCommentValue("");
            }
            setParentId(null);
            setThreadComment(null);
            setThreadReplies([]);

            if (isExternalHistoryMode) {
                await Promise.resolve(onFetchHistory?.(tab));
            } else {
                dispatch(getTripHistory({ history_type: tab, trip_id: id }));
            }
        } catch (err) {
            errorNotify(err);
        } finally {
            setSending(false);
        }
    };

    useEffect(() => {
        if (isStaticMode) return;
        if (isExternalHistoryMode) {
            Promise.resolve(onFetchHistory?.(tab)).catch(() => { });
            return;
        }
        if (!id) return;
        dispatch(getTripHistory({ history_type: tab, trip_id: id }));
    }, [dispatch, id, isExternalHistoryMode, isStaticMode, onFetchHistory, tab]);

    const handleOpenThread = (commentItem) => {
        if (isStaticMode) return;
        setThreadComment(commentItem);
        setThreadReplies(getCommentReplies(commentItem));
        setParentId(getCommentThreadId(commentItem));
    };

    const handleBackFromThread = () => {
        setThreadComment(null);
        setThreadReplies([]);
        setParentId(null);
    };

    const renderCommentCard = (item, index) => {
        if (isStaticMode) {
            return (
                <div className="issues_comments_wrapper" key={item.id || index}>
                    <div>
                        <div>
                            <UserAvatar username={item.user} image={item.image} />
                            <h6>{item.user || "-"}</h6>
                        </div>
                        <div>
                            <p>{item.time || "-"}</p>
                        </div>
                    </div>
                    <div>
                        <p dangerouslySetInnerHTML={{ __html: item.text || "-" }} />
                    </div>
                </div>
            );
        }

        const commentAuthor = getCommentAuthor(item);

        return (
            <div className="issues_comments_wrapper" key={item.id || index}>
                <div>
                    <div>
                        <UserAvatar
                            username={commentAuthor.username}
                            image={commentAuthor.image}
                        />
                        <h6>{commentAuthor.username || "-"}</h6>
                    </div>
                    <div>
                        <p>{formatDate(getCommentCreatedAt(item))}</p>
                    </div>
                </div>
                <div>
                    <p dangerouslySetInnerHTML={{ __html: getCommentBody(item) }} />
                </div>
                {getCommentReplies(item).length > 0 ? (
                    <div
                        className="issue_comment_reply"
                        onClick={() => handleOpenThread(item)}
                        style={{ cursor: "pointer" }}
                    >
                        <p>
                            {getCommentReplies(item).length} Reply
                            {(() => {
                                const seen = new Set();
                                return getCommentReplies(item)
                                    ?.filter((reply) => {
                                        const replyAuthor = getCommentAuthor(reply);
                                        const replyAuthorKey =
                                            String(replyAuthor.id || "").trim() ||
                                            String(replyAuthor.username || "").trim();

                                        if (!replyAuthorKey) return false;
                                        if (seen.has(replyAuthorKey)) return false;
                                        seen.add(replyAuthorKey);
                                        return true;
                                    })
                                    .map((reply, replyIndex) => {
                                        const replyAuthor = getCommentAuthor(reply);
                                        return (
                                        <UserAvatar
                                            key={
                                                String(replyAuthor.id || "").trim() ||
                                                `${replyAuthor.username || "reply-user"}-${replyIndex}`
                                            }
                                            username={replyAuthor.username}
                                            image={replyAuthor.image}
                                        />
                                        );
                                    });
                            })()}
                        </p>
                    </div>
                ) : (
                    <div
                        className="issue_comment_reply"
                        onClick={() => handleOpenThread(item)}
                        style={{ cursor: "pointer" }}
                    >
                        <p>Reply</p>
                    </div>
                )}
            </div>
        );
    };

    const renderLogRow = (item, index) => {
        if (isStaticMode) {
            return (
                <div className="issue_activity_logs" key={item.id || index}>
                    <div>
                        {bulletPoint}
                        <div>{item.text || "-"}</div>
                    </div>
                    <div>{item.time || "-"}</div>
                </div>
            );
        }

        return (
            <div className="issue_activity_logs" key={item.id || index}>
                <div>
                    {bulletPoint}
                    <div>
                        {item.logs && item.logs.before && item.logs.after ? (
                            <span>
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: formatLogAfter(item.logs.before, currentUser),
                                    }}
                                />
                                <span
                                    style={{
                                        margin: "0 4px",
                                        fontWeight: 600,
                                        color: "#4F19E2",
                                    }}
                                >
                                    to
                                </span>
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: formatLogAfter(item.logs.after, currentUser),
                                    }}
                                />
                            </span>
                        ) : item.logs && item.logs.before ? (
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: formatLogAfter(item.logs.before, currentUser),
                                }}
                            />
                        ) : item.logs && item.logs.after ? (
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: formatLogAfter(item.logs.after, currentUser),
                                }}
                            />
                        ) : (
                            "-"
                        )}
                    </div>
                </div>
                <div>{formatDate(item.created_at)}</div>
            </div>
        );
    };

    const renderThreadReplyCard = (reply, rootComment, depth = 0) => {
        const replyAuthor = getCommentAuthor(reply);
        const replyTargetLabel = getReplyTargetLabel(reply, rootComment);
        const nestedReplies = getCommentReplies(reply);
        const replyKey =
            String(getCommentThreadId(reply) ?? "").trim() ||
            `${replyAuthor.username || "reply"}-${depth}`;

        return (
            <div
                key={replyKey}
                className={`issues_comments_wrapper issue_thread_reply_card${depth > 0 ? " is_nested" : ""}`}
                style={depth > 0 ? { marginLeft: `${Math.min(depth, 3) * 18}px` } : undefined}
            >
                <div>
                    <div>
                        <UserAvatar
                            username={replyAuthor.username}
                            image={replyAuthor.image}
                        />
                        <h6>{replyAuthor.username || "-"}</h6>
                    </div>
                    <div>
                        <p>{formatDate(getCommentCreatedAt(reply))}</p>
                    </div>
                </div>
                <div>
                    {replyTargetLabel ? (
                        <div className="issue_thread_reply_to">
                            Replying to <strong>{replyTargetLabel}</strong>
                        </div>
                    ) : null}
                    <p
                        dangerouslySetInnerHTML={{
                            __html: getCommentBody(reply),
                        }}
                    />
                </div>

                {nestedReplies.map((nestedReply) =>
                    renderThreadReplyCard(nestedReply, rootComment, depth + 1)
                )}
            </div>
        );
    };

    const emptyStateMessage =
        tab === "comment"
            ? emptyMessages.comment || DEFAULT_EMPTY_MESSAGES.comment
            : tab === "logs"
                ? emptyMessages.logs || DEFAULT_EMPTY_MESSAGES.logs
                : emptyMessages.all || DEFAULT_EMPTY_MESSAGES.all;

    return (
        <div className="issue_history_wrapper">
            <div className="issue_details_tabs">
                <div
                    onClick={() => {
                        setThreadComment(null);
                        setThreadReplies([]);
                        setTab("all");
                    }}
                    className={tab === "all" ? "active" : ""}
                >
                    {tab === "all" ? activeAllIcon : allIcon} All
                </div>
                <div
                    onClick={() => {
                        setThreadComment(null);
                        setThreadReplies([]);
                        setTab("comment");
                    }}
                    className={tab === "comment" ? "active" : ""}
                >
                    {tab === "comment" ? activeCommentsIcon : commentsIcon} Comments
                </div>
                <div
                    onClick={() => {
                        setThreadComment(null);
                        setThreadReplies([]);
                        setTab("logs");
                    }}
                    className={tab === "logs" ? "active" : ""}
                >
                    {tab === "logs" ? activeActivityIcon : activityIcon} Activity
                </div>
            </div>

            <div className="issue_details_right_side">
                {!isStaticMode && threadComment ? (
                    <>
                        {(() => {
                            const threadAuthor = getCommentAuthor(threadComment);
                            return (
                        <div className="thread_window">
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    marginBottom: 16,
                                    justifyContent: "center",
                                    position: "relative",
                                }}
                            >
                                <button
                                    className="back_btn"
                                    onClick={handleBackFromThread}
                                    style={{ marginRight: 12 }}
                                >
                                    {backButtonIcon}
                                </button>
                                <div className="thread_title">
                                    Thread by
                                    <span
                                        style={{
                                            fontWeight: 600,
                                            textTransform: "capitalize",
                                            marginLeft: "5px",
                                        }}
                                    >
                                        <UserAvatar
                                            username={threadAuthor.username}
                                            image={threadAuthor.image}
                                        />
                                        {threadAuthor.username || "-"}
                                    </span>
                                </div>
                            </div>

                            <div className="issues_comments_wrapper" style={{ marginBottom: 16 }}>
                                <div>
                                    <div>
                                        <UserAvatar
                                            username={threadAuthor.username}
                                            image={threadAuthor.image}
                                        />
                                        <h6>{threadAuthor.username || "-"}</h6>
                                    </div>
                                    <div>
                                        <p>{formatDate(getCommentCreatedAt(threadComment))}</p>
                                    </div>
                                </div>
                                <div>
                                    <p
                                        dangerouslySetInnerHTML={{
                                            __html: getCommentBody(threadComment),
                                        }}
                                    />
                                </div>
                            </div>

                            {threadReplies.map((reply) =>
                                renderThreadReplyCard(reply, threadComment)
                            )}
                        </div>
                            );
                        })()}

                        {!hideComposer ? (
                            <div className="issue_comment_input" style={{ marginTop: 24 }}>
                                <ReactQuill
                                    placeholder="Reply to comment..."
                                    modules={resolvedModules}
                                    ref={quillRef}
                                    theme="snow"
                                    formats={resolvedFormats}
                                    name="comment"
                                    className="react_quill_input quill-small-font"
                                    value={resolvedCommentValue}
                                    onChange={handleCommentChange}
                                />
                                <span
                                    className="send_icon"
                                    style={{
                                        cursor: sending ? "not-allowed" : "pointer",
                                        opacity: sending ? 0.5 : 1,
                                    }}
                                    onClick={sending ? undefined : handleSend}
                                >
                                    {sendMsgIcon}
                                </span>
                            </div>
                        ) : null}
                    </>
                ) : (
                    <>
                        <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                            {loading && filteredItems.length === 0 ? (
                                <div className="no-items text-center">Loading...</div>
                            ) : filteredItems.length === 0 ? (
                                <div className="no-items text-center">{emptyStateMessage}</div>
                            ) : (
                                filteredItems.map((item, index) =>
                                    isCommentItem(item)
                                        ? renderCommentCard(item, index)
                                        : renderLogRow(item, index)
                                )
                            )}
                        </div>

                        {!hideComposer ? (
                            <div className="issue_comment_input">
                                <ReactQuill
                                    placeholder="Write a comment..."
                                    modules={resolvedModules}
                                    ref={quillRef}
                                    theme="snow"
                                    formats={resolvedFormats}
                                    name="comment"
                                    className="react_quill_input quill-small-font"
                                    value={resolvedCommentValue}
                                    onChange={handleCommentChange}
                                />
                                <span
                                    className="send_icon"
                                    style={{
                                        cursor: sending && !isStaticMode ? "not-allowed" : "pointer",
                                        opacity: sending && !isStaticMode ? 0.5 : 1,
                                    }}
                                    onClick={sending && !isStaticMode ? undefined : handleSend}
                                >
                                    {sendMsgIcon}
                                </span>
                            </div>
                        ) : null}
                    </>
                )}
            </div>
        </div>
    );
};

export default LogsComments;
